import { useSession } from '@/components/session-provider';
import { Avatar, AvatarFallback, AvatarImage } from '@sixam/ui/avatar';
import { Button } from '@sixam/ui/button';
import { Icon } from '@sixam/ui/icon';
import { Popover, PopoverContent, PopoverTrigger } from '@sixam/ui/popover';
import { Text } from '@sixam/ui/text';
import { getInitials } from '@sixam/supabase/auth';
import { cn } from '@sixam/ui/utils';
import type { TriggerRef } from '@rn-primitives/popover';
import { router } from 'expo-router';
import { LogOutIcon, PlusIcon, SettingsIcon } from 'lucide-react-native';
import * as React from 'react';
import { View } from 'react-native';

export function UserMenu() {
  const { session, profile, signOut } = useSession();
  const popoverTriggerRef = React.useRef<TriggerRef>(null);

  const email = session?.user.email ?? '';
  const displayName = profile?.full_name ?? email;
  const initials = getInitials(email);
  const avatarUrl = session?.user.user_metadata.avatar_url as string | undefined;

  async function onSignOut() {
    popoverTriggerRef.current?.close();
    await signOut();
    router.replace('/(public)/sign-in');
  }

  async function onAddAccount() {
    popoverTriggerRef.current?.close();
    await signOut();
    router.push('/(public)/sign-in');
  }

  return (
    <Popover>
      <PopoverTrigger asChild ref={popoverTriggerRef}>
        <Button variant="ghost" size="icon" className="size-8 rounded-full">
          <UserAvatar displayName={displayName} initials={initials} avatarUrl={avatarUrl} />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="center" side="bottom" className="w-80 p-0">
        <View className="gap-3 border-b border-border p-3">
          <View className="flex-row items-center gap-3">
            <UserAvatar
              className="size-10"
              displayName={displayName}
              initials={initials}
              avatarUrl={avatarUrl}
            />
            <View className="flex-1">
              <Text className="font-medium leading-5">{displayName}</Text>
              {email ? (
                <Text className="text-sm font-normal leading-4 text-muted-foreground">{email}</Text>
              ) : null}
            </View>
          </View>
          <View className="flex-row flex-wrap gap-3 py-0.5">
            <Button
              variant="outline"
              size="sm"
              onPress={() => {
                popoverTriggerRef.current?.close();
                router.push('/app/me');
              }}
            >
              <Icon as={SettingsIcon} className="size-4" />
              <Text>Manage Account</Text>
            </Button>
            <Button variant="outline" size="sm" className="flex-1" onPress={onSignOut}>
              <Icon as={LogOutIcon} className="size-4" />
              <Text>Sign Out</Text>
            </Button>
          </View>
        </View>
        <Button
          variant="ghost"
          size="lg"
          className="h-16 justify-start gap-3 rounded-none rounded-b-md px-3 sm:h-14"
          onPress={onAddAccount}
        >
          <View className="size-10 items-center justify-center">
            <View className="size-7 items-center justify-center rounded-full border border-dashed border-border bg-muted/50">
              <Icon as={PlusIcon} className="size-5" />
            </View>
          </View>
          <Text>Add account</Text>
        </Button>
      </PopoverContent>
    </Popover>
  );
}

type UserAvatarProps = Omit<React.ComponentProps<typeof Avatar>, 'alt'> & {
  displayName: string;
  initials: string;
  avatarUrl?: string;
};

function UserAvatar({ className, displayName, initials, avatarUrl, ...props }: UserAvatarProps) {
  return (
    <Avatar alt={`${displayName}'s avatar`} className={cn('size-8', className)} {...props}>
      {avatarUrl ? <AvatarImage source={{ uri: avatarUrl }} /> : null}
      <AvatarFallback>
        <Text>{initials}</Text>
      </AvatarFallback>
    </Avatar>
  );
}
